import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AuthenticationService } from '@services/authentication.service';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { IAccount } from '@services/accounts.service';
import { IOrganisationsHierarchy, OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { MatMenu } from '@angular/material/menu';

@Component({
	selector: 'app-dashboard-watch-list',
	templateUrl: './dashboard-watch-list.component.html',
	styleUrls: ['./dashboard-watch-list.component.scss']
})
export class DashboardWatchListComponent implements OnInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** ruleEvents */
	public watchListAssets: LevelAsset[] = [];

	/** fields */
	public fields: Object = { text: 'Name', id: 'AssetID' };

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	public locationUID: string = "";

	/** noWatchList */
	@Output() noWatchList = new EventEmitter<boolean>();

	/** panelData */
	@Input() panelData: any;

	/** creating */
	@Input() creating: boolean;

	/** titleMenu */
	@Input() titleMenu: MatMenu;

	/** assetsToWatch */
	public assetsToWatch: LevelAsset[] = [];

	/** ruleEvents */
	public flaggedAssets: any[] = [];

	/** exampleData */
	public exampleData = [
		{ Id: 0, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 1, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 2, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 3, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 4, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 5, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 6, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 7, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 8, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 9, Name: "Example Asset Name", Description: "Example Asset Description" },
		{ Id: 10, Name: "Example Asset Name", Description: "Example Asset Description" },
	];

	/**
	 * Default Constructor
	 * @param assetsDataService assetsDataService  
	 * @param authenticationService authenticationService 
	 * @param organisationDataService organisationDataService 
	 * @param router router 
	 * @param siteSubscriptions siteSubscriptions  
	 * @param route route 
	 * @param globalFunctions globalFunctions  
	 */
	constructor(
		public assetsDataService: AssetsDataService,
		public authenticationService: AuthenticationService,
		public organisationDataService: OrganisationDataService,
		private router: Router,
		public siteSubscriptions: siteSubscriptions,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions
	) {
		this.globalFunctions.displaySpinner(true)
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, queryParamMap]) =>
				!!selectedAccountGUID && !!queryParamMap && !!hierarchicalData && !!selectedOrganisationUID)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID, hierarchicalData, queryParamMap]) => {
				this.accountID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;

				this.hierarchy = JSON.parse(hierarchicalData);

				this.getWatchListAssets();
				this.getFlaggedAssets();
				// this.subscribeOrganisationStructure(this.accountID, this.OrganisationUID)
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/** 
	 * ngOnInit
	 */
	ngOnInit(): void {
		if (this.panelData) {
			this.assetsToWatch = this.panelData.assets;
		}
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getWatchListAssets
	 */
	getWatchListAssets() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getWatchListAssets$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (watchList: any) => {
				if (this.assetsToWatch.length > 0) {
					this.watchListAssets = watchList.filter((x: LevelAsset) =>
						this.assetsToWatch.map((y: LevelAsset) => y.AssetID).includes(x.AssetID));
				} else {
					this.watchListAssets = watchList.filter((x: LevelAsset) =>
						this.flattenAssets(this.hierarchy).map((y) => y.AssetID).includes(x.AssetID));
				}
				this.watchListAssets.length == 0 ? this.noWatchList.emit(true) : this.noWatchList.emit(false);
			},
			error: (error) => {
				if (error.status === 404) {
					this.watchListAssets = [];
					this.noWatchList.emit(true);
					// console.log("Caught 404");
				}
			}
		});
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/** goToFunctionalLocation */
	goToFunctionalLocation(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`/${this.accountID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`])

		// this.globalFunctions.displaySpinner(true);
		// this.findLocationUID(asset.AssetID).then((location: string) => {
		// 	this.siteSubscriptions.SetSelectedAsset(asset);
		// 	this.globalFunctions.displaySpinner(false);
		// 	this.router.navigate([`/organisations/${this.OrganisationUID}/location/${location}/asset/${asset.AssetID}`])
		// });
	}

	/**
	 * findLocationUID
	 * @param hierarchy
	 */
	findLocationUID(assetID: number): Promise<string> {
		return new Promise((resolve, reject) => {
			// Create a local Subject to control the subscription lifecycle
			const fetchDataSubject = new Subject<void>();

			this.assetsDataService.getLocationUIDfromAssetID$(this.accountID, this.OrganisationUID, assetID).pipe(
				takeUntil(fetchDataSubject),
				distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					resolve(response[0].Data); // Return the desired value
				},
				error: (error) => {
					console.error(error);
					this.globalFunctions.HandleError(error, "dashboardWatchList: error patching asset");
					resolve(""); // Return an empty string on error
				}
			});
		});
	}

	/**
	 * goToWatchList
	 * @param asset asset 
	 */
	goToWatchList(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`${this.accountID}/organisation/${this.OrganisationUID}/watch/${asset.AssetID}`])
	}

	/**
	 * getWatchListAssets
	 */
	getFlaggedAssets() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.getFlaggedAssets$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (flagged: any) => {
				let list: LevelAsset[] = this.flattenAssets(this.hierarchy);
				let listAssetIDs: number[] = [];
				list.forEach(asset => {
					listAssetIDs.push(asset.AssetID);
				});
				let parsedFlagged: any[] = [];
				if (flagged[0].Data) {
					parsedFlagged = JSON.parse(flagged[0].Data);
				}
				this.flaggedAssets = parsedFlagged.filter((x: any) => listAssetIDs.includes(x.AssetID));
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getFlaggedAssets$", false);
				}
			}
		});
	}

	/**
	 * goToFlagged
	 * @param asset asset 
	 */
	goToFlagged(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`${this.accountID}/organisation/${this.OrganisationUID}/flagged`])
	}
}
